// Uma anotação no PDF — o mesmo desenho da tela (NotaFormatada).
//
// A anotação é escrita no Markdown restrito dos relatórios; sem isto o papel
// sairia com os asteriscos e os tracinhos à mostra. Título em caixa alta, um
// item por linha com travessão e linha fina entre os itens, e o "ATENÇÃO"
// (citação) em caixa vermelha. Puro: testável fora do navegador.
import { lerMarkdown, textoPuro, type Trecho } from '@medicov1/shared';
import { prepararNota } from './prepararNota';

type Margem = [number, number, number, number];

export interface CoresDaNota { texto: string; medio: string; linha: string; alerta: string }

export function notaNoPdf(texto: string, tamanho: number, cores: CoresDaNota): { stack: unknown[] } {
  const trechos = (t: Trecho[]) => t.map((x) => ({
    text: x.texto,
    ...(x.negrito ? { bold: true, color: cores.texto } : {}),
    ...(x.italico ? { italics: true } : {}),
  }));
  const stack: unknown[] = [];

  for (const b of lerMarkdown(prepararNota(texto))) {
    if (b.tipo === 'titulo') {
      stack.push({
        text: textoPuro(b.texto).toUpperCase(), bold: true, color: cores.texto,
        fontSize: tamanho + 0.3, characterSpacing: 0.3, margin: [0, 0, 0, 3] as Margem,
      });
    } else if (b.tipo === 'paragrafo') {
      stack.push({ text: trechos(b.texto), fontSize: tamanho, color: cores.medio, lineHeight: 1.3, margin: [0, 0, 0, 4] as Margem });
    } else if (b.tipo === 'lista') {
      stack.push({
        margin: [0, 0, 0, 4] as Margem,
        table: {
          widths: [10, '*'],
          body: b.itens.map((it, i) => [
            { text: b.ordenada ? `${i + 1}.` : '—', bold: true, color: b.ordenada ? cores.texto : cores.alerta, fontSize: tamanho },
            { text: trechos(it), fontSize: tamanho, color: cores.medio, lineHeight: 1.3 },
          ]),
        },
        layout: {
          hLineWidth: (i: number, node: { table: { body: unknown[] } }) => (i === 0 || i === node.table.body.length ? 0 : 0.4),
          vLineWidth: () => 0, hLineColor: () => cores.linha,
          paddingLeft: () => 0, paddingRight: () => 2, paddingTop: () => 3, paddingBottom: () => 3,
        },
      });
    } else if (b.tipo === 'citacao') {
      stack.push({
        margin: [0, 2, 0, 5] as Margem,
        table: {
          widths: ['*'],
          body: [[{ stack: b.paragrafos.map((p) => ({ text: trechos(p), fontSize: tamanho, color: cores.texto, lineHeight: 1.3 })) }]],
        },
        layout: {
          hLineWidth: () => 1, vLineWidth: () => 1,
          hLineColor: () => cores.alerta, vLineColor: () => cores.alerta,
          paddingLeft: () => 6, paddingRight: () => 6, paddingTop: () => 4, paddingBottom: () => 4,
        },
      });
    } else if (b.tipo === 'tabela') {
      for (const linha of b.linhas) {
        stack.push({ text: linha.map((c) => textoPuro(c)).join(' · '), fontSize: tamanho, color: cores.medio, margin: [0, 0, 0, 2] as Margem });
      }
    }
  }
  return { stack };
}
